import { isNative, onPhone, serverOrigin, setServerOrigin, setTunnelKey, testLogin, tunnelKey } from "../platform";
import { useEffect, useRef, useState } from "preact/hooks";
import { api, ApiError, getAccounts, getSession, MAX_ACCOUNTS, switchAccount } from "../api";
import { AvatarPicker } from "./Avatar";
import { LinkLogin } from "./Link";

/** Log in. With onCancel, this adds another account (the current one stays). */
export function Login({ onCancel }: { onCancel?: () => void } = {}) {
  const [step, setStep] = useState<"phone" | "code">("phone");
  const [cc, setCc] = useState("+91");
  const [number, setNumber] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState<string>();
  const [pinToken, setPinToken] = useState<string | null>(null);
  // Like WhatsApp, only the phone app logs in with an SMS code; the web and
  // desktop are linked from the phone (a test server lets test browsers in).
  const smsAllowed = isNative || onPhone || testLogin();
  const [qr, setQr] = useState(!smsAllowed);
  const [pin, setPin] = useState("");
  const [resendAt, setResendAt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [, tick] = useState(0);

  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  async function request(e?: Event) {
    e?.preventDefault();
    setError("");
    // Already logged in here: just switch to it (a second login would be a
    // second device for the same account in the same store).
    const digits = (cc + number).replace(/\D/g, "");
    const known = getAccounts().find((a) => a.user.phone.replace(/\D/g, "") === digits);
    if (known) return known.xmpp.jid === getSession()?.xmpp.jid ? onCancel?.() : switchAccount(known.xmpp.jid);
    if (onCancel && getAccounts().length >= MAX_ACCOUNTS) return setError(`You can add up to ${MAX_ACCOUNTS} accounts on this device.`);
    setBusy(true);
    try {
      const r = await api.requestOtp(cc + number);
      setPhone(r.phone);
      setDevCode(r.dev_code);
      setResendAt(Date.now() + r.resend_in * 1000);
      setStep("code");
      setCode("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function verify(value: string) {
    setError("");
    setBusy(true);
    try {
      const r = await api.verifyOtp(phone, value); // a session swaps the screen
      if ("pin_required" in r) setPinToken(r.pin_token);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      setCode("");
    } finally {
      setBusy(false);
    }
  }

  async function submitPin(value: string) {
    setError("");
    setBusy(true);
    try {
      await api.verifyPin(pinToken!, value);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPin("");
      if (err instanceof ApiError && err.code === "pin_expired") setPinToken(null), setStep("phone");
    } finally {
      setBusy(false);
    }
  }

  const wait = Math.max(0, Math.ceil((resendAt - Date.now()) / 1000));

  if (qr) return <LinkLogin onCancel={onCancel} smsLogin={smsAllowed ? () => setQr(false) : undefined} />;

  if (pinToken)
    return (
      <div class="auth">
        <div class="auth-card">
          <h2>Two-step verification</h2>
          <p class="muted">Enter the 6-digit PIN you created for {phone}.</p>
          <CodeInput value={pin} disabled={busy} onChange={setPin} onComplete={submitPin} label="Two-step verification PIN" />
          {error && <p class="error">{error}</p>}
        </div>
      </div>
    );

  return (
    <div class="auth">
      <div class="auth-card">
        <div class="auth-logo">
          <img src="/icon.svg" alt="" width={56} height={56} />
          <h1>xmm</h1>
        </div>
        {step === "phone" ? (
          <form onSubmit={request}>
            <h2>Enter your phone number</h2>
            <p class="muted">We'll send a 6-digit code to verify it.</p>
            <div class="phone-row">
              <input class="cc" value={cc} onInput={(e) => setCc(e.currentTarget.value)} aria-label="Country code" inputMode="tel" />
              <input
                autoFocus
                value={number}
                onInput={(e) => setNumber(e.currentTarget.value)}
                placeholder="Phone number"
                aria-label="Phone number"
                inputMode="tel"
                autoComplete="tel-national"
              />
            </div>
            {error && <p class="error">{error}</p>}
            <button class="primary" disabled={busy || number.replace(/\D/g, "").length < 6}>
              {busy ? "Sending…" : "Next"}
            </button>
            {onPhone ? (
              <>
                <p class="muted small login-primary-note">
                  This becomes your main phone: if xmm is on another phone, it and its linked devices are logged out, and your chats come back only from a
                  backup (Google Drive, iCloud or a file).
                </p>
                <button type="button" class="link" onClick={() => setQr(true)}>
                  Already use xmm on another phone? Link this one as a companion
                </button>
              </>
            ) : (
              !isNative && (
                <button type="button" class="link" onClick={() => setQr(true)}>
                  Link with a QR code instead
                </button>
              )
            )}
            {onCancel && (
              <button type="button" class="link" onClick={onCancel}>
                Cancel: back to {getSession()?.user.name || "your account"}
              </button>
            )}
            {isNative && <ServerSetting />}
          </form>
        ) : (
          <div>
            <h2>Verify {phone}</h2>
            <p class="muted">
              Enter the code we sent.{" "}
              <a href="#" onClick={(e) => (e.preventDefault(), setStep("phone"))}>
                Wrong number?
              </a>
            </p>
            <CodeInput value={code} disabled={busy} onChange={setCode} onComplete={verify} />
            {devCode && (
              <p class="dev-hint">
                Dev mode code: <button class="link" onClick={() => (setCode(devCode), verify(devCode))}>{devCode}</button>
              </p>
            )}
            {error && <p class="error">{error}</p>}
            <button class="link" disabled={wait > 0 || busy} onClick={() => request()}>
              {wait > 0 ? `Resend code in ${wait}s` : "Resend code"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** The app: which xmm server to use (the web client is served by it). */
function ServerSetting() {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState(serverOrigin());
  const [key, setKey] = useState(tunnelKey());
  const [error, setError] = useState("");
  if (!open)
    return (
      <p class="muted small">
        Server: {serverOrigin()}{" "}
        <button type="button" class="link" onClick={() => setOpen(true)}>
          Change
        </button>
      </p>
    );
  return (
    <div class="server-setting">
      <input class="line-input" value={url} onInput={(e) => setUrl(e.currentTarget.value)} aria-label="Server address" placeholder="https://chat.example.com" />
      <input class="line-input" value={key} onInput={(e) => setKey(e.currentTarget.value)} aria-label="Tunnel key" placeholder="Tunnel key (only for a dev tunnel)" />
      {error && <p class="error">{error}</p>}
      <button
        type="button"
        class="primary"
        onClick={async () => {
          try {
            const origin = new URL(url.trim()).origin;
            const r = await fetch(origin + "/api/v1/config", { headers: { "X-Xmm-Tunnel": key.trim(), "ngrok-skip-browser-warning": "1" } });
            if (!r.ok) throw new Error();
            setServerOrigin(origin);
            setTunnelKey(key);
            location.reload();
          } catch {
            setError("Can't reach an xmm server at that address");
          }
        }}
      >
        Save
      </button>
    </div>
  );
}

function CodeInput(props: { value: string; disabled: boolean; onChange: (v: string) => void; onComplete: (v: string) => void; label?: string }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => ref.current?.focus(), [props.disabled]);
  return (
    <input
      ref={ref}
      class="code-input"
      value={props.value}
      disabled={props.disabled}
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={6}
      placeholder="––– –––"
      aria-label={props.label ?? "Verification code"}
      onInput={(e) => {
        const v = e.currentTarget.value.replace(/\D/g, "").slice(0, 6);
        props.onChange(v);
        if (v.length === 6) props.onComplete(v);
      }}
    />
  );
}

export function ProfileSetup() {
  const me = getSession()?.user;
  const [name, setName] = useState(me?.name ?? "");
  const [avatar, setAvatar] = useState<string | null>(me?.avatar_url ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function save(e: Event) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.updateMe({ name: name.trim() });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="auth">
      <form class="auth-card" onSubmit={save}>
        <h2>Profile info</h2>
        <p class="muted">Please provide your name and an optional profile photo.</p>
        <AvatarPicker url={avatar} jid={me?.jid ?? ""} name={name} onChange={setAvatar} />
        <input autoFocus value={name} maxLength={25} onInput={(e) => setName(e.currentTarget.value)} placeholder="Your name" aria-label="Your name" />
        {error && <p class="error">{error}</p>}
        <button class="primary" disabled={busy || !name.trim()}>
          {busy ? "Saving…" : "Next"}
        </button>
      </form>
    </div>
  );
}
